import { Injectable } from '@angular/core';
import {
  GALLERY_UPLOAD_LIMITS,
  validateGalleryFileSize,
} from './gallery-upload-limits';

export interface ImagePreprocessResult {
  file: File;
  wasModified: boolean;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImagePreprocessorService {
  /**
   * Downscale to HD (max 1920px longest edge) then reduce JPEG quality until under byte limit.
   * GIFs and non-images are returned unchanged.
   */
  async preprocessImage(file: File): Promise<ImagePreprocessResult | null> {
    const mime = file.type.toLowerCase();
    if (!mime.startsWith('image/') || mime === 'image/gif') {
      return null;
    }

    const img = await this.loadImage(file);
    try {
      const { width, height } = this.fitWithinMaxDimension(
        img.width,
        img.height,
        GALLERY_UPLOAD_LIMITS.maxImageDimensionPx
      );

      const needsResize = width !== img.width || height !== img.height;
      const needsCompress = file.size > GALLERY_UPLOAD_LIMITS.maxImageBytes;

      if (!needsResize && !needsCompress) {
        return {
          file,
          wasModified: false,
          width: img.width,
          height: img.height,
        };
      }

      const maxBytes = GALLERY_UPLOAD_LIMITS.maxImageBytes;
      const preferredMime = this.pickOutputMime(mime);

      let processed =
        (await this.renderToFile(img, width, height, file.name, preferredMime, maxBytes)) ??
        (preferredMime !== 'image/jpeg'
          ? await this.renderToFile(img, width, height, file.name, 'image/jpeg', maxBytes)
          : null);

      if (!processed) {
        return null;
      }

      return {
        file: processed,
        wasModified: true,
        width,
        height,
      };
    } finally {
      URL.revokeObjectURL(img.src);
    }
  }

  validateFileForUpload(
    file: File,
    fileType: 'image' | 'gif' | 'video' | 'pdf' | 'attachment'
  ): { valid: boolean; message?: string } {
    return validateGalleryFileSize(file, fileType);
  }

  private pickOutputMime(sourceMime: string): string {
    if (sourceMime === 'image/png' || sourceMime === 'image/webp') {
      return sourceMime;
    }
    return 'image/jpeg';
  }

  private fitWithinMaxDimension(
    width: number,
    height: number,
    maxEdge: number
  ): { width: number; height: number } {
    const longest = Math.max(width, height);
    if (longest <= maxEdge) {
      return { width, height };
    }
    const scale = maxEdge / longest;
    return {
      width: Math.floor(width * scale),
      height: Math.floor(height * scale),
    };
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  }

  private async renderToFile(
    img: HTMLImageElement,
    width: number,
    height: number,
    originalName: string,
    mime: string,
    maxBytes: number
  ): Promise<File | null> {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(img, 0, 0, width, height);

    if (mime === 'image/png' || mime === 'image/webp') {
      const blob = await this.canvasToBlob(canvas, mime);
      if (!blob || blob.size > maxBytes) {
        return null;
      }
      return new File([blob], this.deriveFileName(originalName, mime), { type: mime });
    }

    let quality: number = GALLERY_UPLOAD_LIMITS.initialJpegQuality;
    const minQuality = GALLERY_UPLOAD_LIMITS.minJpegQuality;
    const step = GALLERY_UPLOAD_LIMITS.qualityStep;

    while (quality >= minQuality) {
      const blob = await this.canvasToBlob(canvas, mime, quality);
      if (!blob) {
        return null;
      }
      if (blob.size <= maxBytes) {
        return new File([blob], this.deriveFileName(originalName, mime), {
          type: mime,
        });
      }
      quality = Math.round((quality - step) * 100) / 100;
    }

    return null;
  }

  private canvasToBlob(
    canvas: HTMLCanvasElement,
    mime: string,
    quality?: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (mime === 'image/png') {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
        return;
      }
      canvas.toBlob((blob) => resolve(blob), mime, quality ?? 0.85);
    });
  }

  private deriveFileName(originalName: string, mime: string): string {
    const base = originalName.replace(/\.[^/.]+$/, '');
    const ext =
      mime === 'image/png'
        ? 'png'
        : mime === 'image/webp'
          ? 'webp'
          : 'jpg';
    return `${base}.${ext}`;
  }
}
